import { FC, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import type { Container } from '@/hooks/useBatteryContainers';

const CAPACITY_OPTIONS = [16, 18, 20, 22, 24, 26, 28, 30];

interface EditContainerModalProps {
  open: boolean;
  onClose: () => void;
  container: Container | null;
  onUpdate: (id: string, updates: Partial<Container>) => Promise<void>;
}

export const EditContainerModal: FC<EditContainerModalProps> = ({
  open,
  onClose,
  container,
  onUpdate,
}) => {
  const [name, setName] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [location, setLocation] = useState('');
  const [capacity, setCapacity] = useState<number>(16);
  const [max50kw, setMax50kw] = useState<number>(16);
  const [max100kw, setMax100kw] = useState<number>(8);
  const [status, setStatus] = useState<'onderweg' | 'aangekomen' | 'in_gebruik' | 'leeg'>('onderweg');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (container) {
      setName(container.name);
      setArrivalDate(container.arrival_date ? new Date(container.arrival_date).toISOString().split('T')[0] : '');
      setLocation(container.location || '');
      setCapacity(container.capacity);
      setMax50kw(container.max_batteries_50kw || 16);
      setMax100kw(container.max_batteries_100kw || 8);
      setStatus(container.status);
      setNotes(container.notes || '');
    }
  }, [container]);

  const handleUpdate = async () => {
    if (!container) return;
    
    if (!name.trim()) {
      toast.error('Vul een naam in');
      return;
    }

    setLoading(true);
    try {
      await onUpdate(container.id, {
        name,
        arrival_date: arrivalDate || null,
        location: location || null,
        capacity,
        max_batteries_50kw: max50kw,
        max_batteries_100kw: max100kw,
        status,
        notes: notes || null,
      });
      toast.success('Container bijgewerkt');
      handleClose();
    } catch (error) {
      console.error('Error updating container:', error);
      toast.error('Fout bij bijwerken container');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setArrivalDate('');
    setLocation('');
    setCapacity(16);
    setMax50kw(16);
    setMax100kw(8);
    setStatus('onderweg');
    setNotes('');
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Container Bewerken</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Naam</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Container #1"
            />
          </div>

          <div>
            <Label>Verwachte aankomstdatum</Label>
            <Input
              type="date"
              value={arrivalDate}
              onChange={(e) => setArrivalDate(e.target.value)}
            />
          </div>

          <div>
            <Label>Locatie</Label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Magazijn A"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Max 50kW Batterijen</Label>
              <Input
                type="number"
                min="0"
                value={max50kw}
                onChange={(e) => setMax50kw(parseInt(e.target.value) || 0)}
              />
            </div>

            <div>
              <Label>Max 100kW Batterijen</Label>
              <Input
                type="number"
                min="0"
                value={max100kw}
                onChange={(e) => setMax100kw(parseInt(e.target.value) || 0)}
              />
            </div>
          </div>

          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={(val: any) => setStatus(val)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="onderweg">Onderweg</SelectItem>
                <SelectItem value="aangekomen">Aangekomen</SelectItem>
                <SelectItem value="in_gebruik">In gebruik</SelectItem>
                <SelectItem value="leeg">Leeg</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Notities</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Extra informatie..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Annuleren
          </Button>
          <Button onClick={handleUpdate} disabled={loading}>
            {loading ? 'Bijwerken...' : 'Bijwerken'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
